<template>
  <button @click="downloadPDF" class="btn btn-primary">
    {{ $t("t-pdf-download") }}
  </button>
</template>

<script>
import pdfMake from "pdfmake/build/pdfmake";
// import { vfs } from "pdfmake/build/vfs_fonts";
import { customVfs, customFonts } from "@/assets/fonts/customFonts";

pdfMake.vfs = customVfs;
pdfMake.fonts = customFonts;

// Deine Assets (Logo, Kontaktperson etc.)
import logoLight from "@/assets/images/sander/Sander-Logo-Boxed.jpg";
import contactPersonImg from "@/assets/images/persons/ansprechpartner_1.jpg";

export default {
  name: "PdfDownloadButton",
  props: {
    machine: {
      type: Object,
      required: true,
    },
  },

  computed: {
    /**
     * Bemerkungen (addequip) in Zeilen aufteilen
     */
    formattedBemerkung() {
      if (!this.machine.addequip) return [];
      let text = this.machine.addequip.replace(/<br\s*\/?>/gi, ",");
      let items = text.split(",").map((el) => el.trim());
      return items.filter((el) => el.length > 0);
    },

    primaryColorComputed() {
      return typeof window !== "undefined" && window.getComputedStyle
        ? window
            .getComputedStyle(document.documentElement)
            .getPropertyValue("--primary")
            .trim() || "red"
        : "red";
    },

    /**
     * Gabel-Abmessungen, falls forks vorhanden (L x B x D)
     */
    forksFormatted() {
      if (!this.machine || !this.machine.forks) return null;
      const match = this.machine.forks.match(
        /([\d.,]+)\s*x\s*([\d.,]+)\s*x\s*([\d.,]+)/i
      );
      if (!match) return null;
      const formatFork = (s) => {
        let num = parseFloat(s.replace(/,/g, "."));
        return isNaN(num) ? s : new Intl.NumberFormat("de-DE").format(num);
      };
      return {
        forks_1: formatFork(match[1]),
        forks_2: formatFork(match[2]),
        forks_3: formatFork(match[3]),
      };
    },

    /**
     * Prüft, ob das Gerät ein Anbaugerät (Attachment) ist
     */
    isAttachment() {
      if (!this.machine || !this.machine.machine_type_l) return false;
      const mt = this.machine.machine_type_l;
      return mt === "Anbaugerät" || mt === "Attachment";
    },

    /**
     * Gibt es einen "Beschreibung"-Block? (tyresfront/tyresrear/techcond_l/optcond_l usw.)
     */
    hasBeschreibung() {
      if (!this.machine) return false;
      const m = this.machine;
      const tyresValid =
        m.tyres_l && m.tyres_l !== "Unknown" && m.tyres_l !== "Unbekannt";
      return (
        tyresValid ||
        (m.tyresfront && m.tyresfront != 0) ||
        (m.tyresrear && m.tyresrear != 0) ||
        m.techcond_l ||
        m.optcond_l
      );
    },

    /**
     * Gibt es "Sonderausstattung"? (attachbit_l oder addeqbits)
     */
    hasSonderausstattung() {
      if (!this.machine) return false;
      const attachArray = Array.isArray(this.machine.attachbit_l)
        ? this.machine.attachbit_l
        : this.machine.attachbit_l
        ? [this.machine.attachbit_l]
        : [];
      const addeqArray = this.formattedAddeqbits(this.machine);
      return attachArray.length > 0 || addeqArray.length > 0;
    },
  },

  methods: {
    // --------------------------------------------------------
    // 1) Hilfsfunktionen für QR-Code, Bilder, etc.
    // --------------------------------------------------------

    customTableLayout(color) {
      return {
        hLineWidth: function () {
          return 1;
        },
        vLineWidth: function () {
          return 1;
        },
        hLineColor: function () {
          return color;
        },
        vLineColor: function () {
          return color;
        },
        paddingLeft: function () {
          return 5;
        },
        paddingRight: function () {
          return 5;
        },
        paddingTop: function () {
          return 5;
        },
        paddingBottom: function () {
          return 5;
        },
      };
    },

    async fetchQrCode(url) {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/qrcode?url=${encodeURIComponent(url)}`
        );
        const data = await response.json();
        return data.qr_code; // "data:image/png;base64,..."
      } catch (err) {
        console.error("Fehler beim Laden des QR-Codes", err);
        return null;
      }
    },

    async convertImageToBase64(imageUrl) {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error("Fehler beim Laden eines Bildes:", error);
        return null;
      }
    },

    buildThumbnailColumns(thumbnailImages) {
      const col1 = [];
      const col2 = [];
      const col3 = [];
      thumbnailImages.forEach((img, index) => {
        const imgObj = {
          image: img,
          width: 50,
          margin: [0, 5, 0, 5],
          alignment: "center",
        };
        if (index % 3 === 0) {
          col1.push(imgObj);
        } else if (index % 3 === 1) {
          col2.push(imgObj);
        } else {
          col3.push(imgObj);
        }
      });
      return {
        columns: [
          { stack: col1, width: "*" },
          { stack: col2, width: "*" },
          { stack: col3, width: "*" },
        ],
      };
    },

    createDataRow(
      label,
      value,
      appendColon = true,
      labelWidth = "35%",
      valueWidth = "65%"
    ) {
      if (!value && value !== 0) return { text: "" };
      return {
        columns: [
          {
            text: appendColon ? label + ":" : label,
            style: "dataLabel",
            alignment: "left",
            width: labelWidth,
          },
          {
            text: value,
            style: "dataValue",
            alignment: "right",
            width: valueWidth,
          },
        ],
        margin: [0, 2, 0, 2],
      };
    },

    formatWithThousands(value) {
      if (!value || value === 0) return "-";
      return new Intl.NumberFormat("de-DE").format(value);
    },

    // --------------------------------------------------------
    // 2) Fahrzeug- & Mastdaten ODER Attachment-Daten
    // --------------------------------------------------------
    buildVehicleAndMastRow() {
      if (!this.isAttachment) {
        // =========================================
        // (A) Normale Maschine (Fahrzeug + Mast)
        // =========================================
        return {
          columns: [
            {
              width: "50%",
              stack: [
                {
                  text: this.$t("t-detail-vehicle-data"),
                  style: "sectionHeader",
                },
                // Hersteller
                this.machine.manufacturer
                  ? this.createDataRow(
                      this.$t("t-detail-manufacturer"),
                      this.machine.manufacturer,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Typ
                this.machine.model
                  ? this.createDataRow(
                      this.$t("t-detail-type"),
                      this.machine.model,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Bauart => ABER hier: "t-cards-type-of-construction" (30/70)
                this.machine.toc_l
                  ? this.createDataRow(
                      this.$t("t-cards-type-of-construction"),
                      this.machine.toc_l,
                      true,
                      "30%",
                      "70%"
                    )
                  : {},
                // Antrieb
                this.machine.enginetype_l
                  ? this.createDataRow(
                      this.$t("t-detail-drive"),
                      this.machine.enginetype_l,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Tragkraft != 0
                this.machine.capacity && this.machine.capacity != 0
                  ? this.createDataRow(
                      this.$t("t-detail-capacity"),
                      this.formatWithThousands(this.machine.capacity) + " kg",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Baujahr != 0
                this.machine.yoc && this.machine.yoc != 0
                  ? this.createDataRow(
                      this.$t("t-detail-year"),
                      this.machine.yoc,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Betriebsstunden != 0
                this.machine.hours && this.machine.hours != 0
                  ? this.createDataRow(
                      this.$t("t-detail-hours"),
                      this.machine.hours,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Gewicht != 0
                this.machine.weight && this.machine.weight != 0
                  ? this.createDataRow(
                      this.$t("t-detail-weight"),
                      this.formatWithThousands(this.machine.weight) + " kg",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Interne Nr.
                this.machine.internalno
                  ? this.createDataRow(
                      this.$t("t-detail-internalno"),
                      this.machine.internalno,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
              ],
            },
            {
              width: "50%",
              stack: [
                {
                  text: this.$t("t-detail-mast-data"),
                  style: "sectionHeader",
                },
                // Masttyp
                this.machine.masttype_l
                  ? this.createDataRow(
                      this.$t("t-detail-masttype"),
                      this.machine.masttype_l,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Hubhöhe != 0
                this.machine.liftingheight && this.machine.liftingheight != 0
                  ? this.createDataRow(
                      this.$t("t-detail-liftingheight"),
                      this.formatWithThousands(this.machine.liftingheight) +
                        " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Geschl. Bauhöhe != 0
                this.machine.closedheight && this.machine.closedheight != 0
                  ? this.createDataRow(
                      this.$t("t-detail-closedheight"),
                      this.formatWithThousands(this.machine.closedheight) +
                        " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Freihub != 0
                this.machine.freelift && this.machine.freelift != 0
                  ? this.createDataRow(
                      this.$t("t-detail-freelift"),
                      this.formatWithThousands(this.machine.freelift) + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Gabeln
                this.forksFormatted
                  ? this.createDataRow(
                      this.$t("t-detail-forklength"),
                      this.forksFormatted.forks_1 + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                this.forksFormatted
                  ? this.createDataRow(
                      this.$t("t-detail-forkwidth"),
                      this.forksFormatted.forks_2 + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                this.forksFormatted
                  ? this.createDataRow(
                      this.$t("t-detail-forkthickness"),
                      this.forksFormatted.forks_3 + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Gabelträger
                this.machine.forkcarr
                  ? this.createDataRow(
                      this.$t("t-detail-forkcarr"),
                      this.machine.forkcarr,
                      true,
                      "30%",
                      "70%"
                    )
                  : {},
                // Batterie
                ...(this.machine.battery
                  ? [
                      {
                        text: this.$t("t-detail-engine-battery-charger"),
                        style: "sectionHeader",
                        margin: [0, 10, 0, 2],
                      },
                      this.createDataRow(
                        this.$t("t-detail-battery"),
                        this.machine.battery,
                        true,
                        "20%",
                        "80%"
                      ),
                    ]
                  : []),
              ],
            },
          ],
          columnGap: 10,
          margin: [0, 5, 0, 5],
        };
      } else {
        // =========================================
        // (B) Anbaugerät (Attachment)
        // =========================================
        const fromVal = Number(this.machine.rangefrom) || 0;
        const toVal = Number(this.machine.rangeto) || 0;
        let openWidthVal = "";
        if (fromVal || toVal) {
          openWidthVal = toVal === 0 ? `${fromVal}` : `${fromVal} - ${toVal}`;
        }
        const isoVal =
          this.machine.isoclass && this.machine.isoclass !== "keine"
            ? this.machine.isoclass
            : "";

        return {
          columns: [
            {
              width: "50%",
              stack: [
                {
                  text: this.$t("t-detail-device-data"),
                  style: "sectionHeader",
                },
                // Hersteller
                this.machine.manufacturer
                  ? this.createDataRow(
                      this.$t("t-detail-manufacturer"),
                      this.machine.manufacturer,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Typ
                this.machine.model
                  ? this.createDataRow(
                      this.$t("t-detail-type"),
                      this.machine.model,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Bauart => "t-cards-type-of-construction" (30/70)
                this.machine.toc_l
                  ? this.createDataRow(
                      this.$t("t-cards-type-of-construction"),
                      this.machine.toc_l,
                      true,
                      "30%",
                      "70%"
                    )
                  : {},
                // Antrieb
                this.machine.enginetype_l
                  ? this.createDataRow(
                      this.$t("t-detail-drive"),
                      this.machine.enginetype_l,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Tragkraft != 0
                this.machine.capacity && this.machine.capacity != 0
                  ? this.createDataRow(
                      this.$t("t-detail-capacity"),
                      this.formatWithThousands(this.machine.capacity) + " kg",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Offene Weite
                openWidthVal
                  ? this.createDataRow(
                      this.$t("t-open-width"),
                      openWidthVal + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // ISO-Klasse
                isoVal
                  ? this.createDataRow(
                      this.$t("t-iso-class"),
                      isoVal,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Lastschwerpunkt != 0
                this.machine.loadcenter && this.machine.loadcenter != 0
                  ? this.createDataRow(
                      this.$t("t-load-centre-of-gravity"),
                      this.machine.loadcenter + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Baujahr != 0
                this.machine.yoc && this.machine.yoc != 0
                  ? this.createDataRow(
                      this.$t("t-detail-year"),
                      this.machine.yoc,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Betriebsstunden != 0
                this.machine.hours && this.machine.hours != 0
                  ? this.createDataRow(
                      this.$t("t-detail-hours"),
                      this.machine.hours,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Gewicht != 0
                this.machine.weight && this.machine.weight != 0
                  ? this.createDataRow(
                      this.$t("t-detail-weight"),
                      this.formatWithThousands(this.machine.weight) + " kg",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // Interne Nr.
                this.machine.internalno
                  ? this.createDataRow(
                      this.$t("t-detail-internalno"),
                      this.machine.internalno,
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
              ],
            },
            {
              width: "50%",
              stack: [
                {
                  text: this.$t("t-detail-dimensions"),
                  style: "sectionHeader",
                },
                // carrbroad != 0
                this.machine.carrbroad && this.machine.carrbroad != 0
                  ? this.createDataRow(
                      this.$t("t-detail-back-broad"),
                      this.machine.carrbroad + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // balancepoint != 0
                this.machine.balancepoint && this.machine.balancepoint != 0
                  ? this.createDataRow(
                      this.$t("t-detail-balancepoint"),
                      this.machine.balancepoint + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // frontend != 0
                this.machine.frontend && this.machine.frontend != 0
                  ? this.createDataRow(
                      this.$t("t-detail-frontend-dimension"),
                      this.machine.frontend + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // armlength != 0
                this.machine.armlength && this.machine.armlength != 0
                  ? this.createDataRow(
                      this.$t("t-detail-armlength"),
                      this.machine.armlength + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // armbroad != 0
                this.machine.armbroad && this.machine.armbroad != 0
                  ? this.createDataRow(
                      this.$t("t-detail-armbroade"),
                      this.machine.armbroad + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
                // armsize != 0
                this.machine.armsize && this.machine.armsize != 0
                  ? this.createDataRow(
                      this.$t("t-detail-armsize"),
                      this.machine.armsize + " mm",
                      true,
                      "56%",
                      "44%"
                    )
                  : {},
              ],
            },
          ],
          columnGap: 10,
          margin: [0, 5, 0, 5],
        };
      }
    },

    // --------------------------------------------------------
    // 3) Beschreibung & Sonderausstattung
    // --------------------------------------------------------
    buildDescriptionAndExtrasRow(attachments) {
      // Falls BEIDES false => Keine Zeile
      if (!this.hasBeschreibung && !this.hasSonderausstattung) {
        return {};
      }

      const columns = [];

      // Linke Spalte: Beschreibung
      if (this.hasBeschreibung) {
        columns.push({
          width: "50%",
          stack: [
            {
              text: this.$t("t-detail-description"),
              style: "sectionHeader",
            },
            // Reifen VORN
            this.machine.tyres_l &&
            this.machine.tyres_l !== "Unknown" &&
            this.machine.tyres_l !== "Unbekannt"
              ? this.createDataRow(
                  this.$t("t-detail-tyresfront-lbl"),
                  this.machine.tyres_l
                )
              : {},
            this.machine.tyresfront && this.machine.tyresfront != 0
              ? this.createDataRow(
                  this.$t("t-detail-tyresfront-size"),
                  this.machine.tyresfront
                )
              : {},
            // Reifen HINTEN
            this.machine.tyres_l &&
            this.machine.tyres_l !== "Unknown" &&
            this.machine.tyres_l !== "Unbekannt"
              ? this.createDataRow(
                  this.$t("t-detail-tyresrear-lbl"),
                  this.machine.tyres_l
                )
              : {},
            this.machine.tyresrear && this.machine.tyresrear != 0
              ? this.createDataRow(
                  this.$t("t-detail-tyresrear-size"),
                  this.machine.tyresrear
                )
              : {},
            // Technischer Zustand
            this.machine.techcond_l
              ? this.createDataRow(
                  this.$t("t-detail-techcond"),
                  this.machine.techcond_l,
                  true,
                  "65%",
                  "35%"
                )
              : {},
            // Optischer Zustand
            this.machine.optcond_l
              ? this.createDataRow(
                  this.$t("t-detail-optcond"),
                  this.machine.optcond_l,
                  true,
                  "65%",
                  "35%"
                )
              : {},
          ],
        });
      }

      // Rechte Spalte: Sonderausstattung
      if (this.hasSonderausstattung) {
        columns.push({
          width: "50%",
          stack: [
            {
              text: this.$t("t-detail-extras"),
              style: "sectionHeader",
            },
            // Attachments
            this.machine.attachbit_l && this.machine.attachbit_l.length
              ? [
                  {
                    text: this.$t("t-detail-attachments"),
                    style: "dataLabel",
                    margin: [0, 2, 0, 2],
                  },
                  {
                    ul: attachments,
                    style: "listValue",
                    margin: [0, 2, 0, 2],
                  },
                ]
              : [],
            // addeqbits
            this.formattedAddeqbits(this.machine).length
              ? [
                  {
                    text: this.$t("t-detail-other-equipment"),
                    style: "dataLabel",
                    margin: [0, 2, 0, 2],
                  },
                  {
                    ul: this.formattedAddeqbits(this.machine),
                    style: "listValue",
                    margin: [0, 2, 0, 2],
                  },
                ]
              : [],
          ].flat(),
        });
      }

      return {
        columns,
        columnGap: 10,
        margin: [0, 2, 0, 5],
      };
    },

    // --------------------------------------------------------
    // 4) Bemerkungen
    // --------------------------------------------------------
    buildBemerkungRow() {
      if (!this.formattedBemerkung || !this.formattedBemerkung.length) {
        return {};
      }
      const chunkSize = this.formattedBemerkung.length > 9 ? 4 : 3;
      const chunks = [];
      for (let i = 0; i < this.formattedBemerkung.length; i += chunkSize) {
        chunks.push(this.formattedBemerkung.slice(i, i + chunkSize));
      }
      const columns = chunks.map((chunk) => ({
        ul: chunk,
        style: "listValue",
        margin: [0, 2, 0, 2],
        width: "*",
      }));
      return {
        stack: [
          {
            text: this.$t("t-detail-comments"),
            style: "sectionHeader",
            margin: [0, 10, 0, 5],
          },
          {
            columns,
            columnGap: 10,
          },
        ],
        margin: [0, 2, 0, 5],
      };
    },

    // --------------------------------------------------------
    // 5) Kontakt & Preis
    // --------------------------------------------------------
    buildContactAndPriceRow(contactImgBase64) {
      return {
        columns: [
          {
            width: "50%",
            stack: [
              {
                text: this.$t("t-detail-contact"),
                style: "sectionHeader",
                margin: [0, 0, 0, 5],
              },
              contactImgBase64
                ? {
                    image: contactImgBase64,
                    width: 170,
                    margin: [0, 0, 0, 10],
                  }
                : {},
              {
                text: "Thomas Krumpöck",
                style: "contactName",
                margin: [0, 2, 0, 0],
              },
              {
                text: this.$t("t-detail-contact-title"),
                style: "contactTitle",
                margin: [0, 0, 0, 2],
              },
              this.createDataRow(
                this.$t("t-detail-contact-phone"),
                "+49 (0)371 52338-486",
                false,
                "20%",
                "80%"
              ),
              this.createDataRow(
                this.$t("t-detail-contact-mobile"),
                "+49 (0)151 54433809",
                false,
                "20%",
                "80%"
              ),
              this.createDataRow(
                this.$t("t-detail-contact-email"),
                "gebrauchtstapler@sander-ft.de",
                false,
                "20%",
                "80%"
              ),
            ],
          },
          {
            width: "50%",
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    stack: [
                      {
                        text: this.machine.custprice
                          ? this.formatWithThousands(this.machine.custprice) +
                            " EUR"
                          : "",
                        style: "priceMain",
                      },
                      {
                        text:
                          " (" +
                          this.$t("t-detail-taxes") +
                          ") " +
                          this.$t("t-detail-subject-to-prior-sale"),
                        style: "priceNote",
                        margin: [0, 2, 0, 2],
                      },
                    ],
                    alignment: "center",
                  },
                ],
              ],
            },
            layout: this.customTableLayout(this.primaryColorComputed),
            fillColor: "white",
          },
        ],
        columnGap: 10,
        margin: [0, 2, 0, 5],
      };
    },

    // --------------------------------------------------------
    // 6) addeqbits => Array
    // --------------------------------------------------------
    formattedAddeqbits(machine) {
      if (!machine || !machine.addeqbits_l) return [];
      let s = machine.addeqbits_l;
      if (Array.isArray(s)) {
        return s.map((item) => item.trim()).filter((item) => item.length);
      }
      if (typeof s !== "string") {
        s = String(s);
      }
      s = s.replace(/[{}]/g, "");
      return s
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length);
    },

    // --------------------------------------------------------
    // 7) PDF erzeugen
    // --------------------------------------------------------
    async downloadPDF() {
      const primaryColor = this.primaryColorComputed;

      // A) Kontaktfoto laden
      const contactImageBase64 = await this.convertImageToBase64(
        contactPersonImg
      );

      // B) URL + QR-Code
      const machineLink = `${process.env.VUE_APP_URL}/products/${this.machine.internalno}`;
      const qrCodeBase64 = await this.fetchQrCode(machineLink);

      // C) Bilderlimit (max. 13)
      const imageUrls = this.machine.imageUrls || [];
      const limitedImages = imageUrls.slice(0, 13);
      const base64Images = [];
      for (let i = 0; i < limitedImages.length; i++) {
        const base64 = await this.convertImageToBase64(limitedImages[i].src);
        if (base64) base64Images.push(base64);
      }
      const heroImage = base64Images[0] || null;
      const thumbnailImages = base64Images.slice(1);

      // D) Logo
      const logoBase64 = await this.convertImageToBase64(logoLight);

      // E) Attachments
      const attachments = this.machine.attachbit_l
        ? JSON.parse(JSON.stringify(this.machine.attachbit_l))
        : [];

      // G) Footer-Texte
      const printDateText =
        this.$t("t-printdate") + ": " + new Date().toLocaleDateString("de-DE");
      const footerPrivacy = this.$t("t-pdf-privacy-notice");

      // H) Header (Titel zweizeilig)
      const headerTable = {
        table: {
          widths: ["*", "auto"],
          body: [
            [
              {
                stack: [
                  {
                    text:
                      this.machine.manufacturer.toUpperCase() +
                      " " +
                      this.machine.toc_l.toUpperCase(),
                    style: "header",
                    margin: [0, 20, 0, 0],
                  },
                  {
                    text: this.machine.model.toUpperCase(),
                    style: "subheader",
                    color: primaryColor,
                  },
                ],
              },
              {
                image: logoBase64,
                width: 150,
                alignment: "right",
                valign: "top",
                margin: [0, -11, -18, 0],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0],
      };

      // I) docDefinition
      const docDefinition = {
        pageMargins: [40, 15, 40, 115],
        defaultStyle: {
          font: "DaxOffcPro",
          fontSize: 8,
        },
        info: {
          title: "Maschinendetails",
          author: "Sander FT",
          subject: "Details zu einer Gebrauchtmaschine",
          keywords: "Gebrauchtmaschine, PDF",
        },
        // Hintergrund für den Inhaltsbereich in #e9e9e9, mit weißem Außenrand
        background: (currentPage, pageSize) => {
          return [
            {
              canvas: [
                {
                  type: "rect",
                  x: 15,
                  y: 15,
                  w: pageSize.width - 30, // links + rechts je 40
                  h: pageSize.height - 140, // oben 40, unten 140
                  color: "#e9e9e9",
                },
              ],
            },
          ];
        },
        content: [
          headerTable,
          {
            columns: [
              {
                width: "33%",
                stack: [
                  heroImage
                    ? {
                        image: heroImage,
                        width: 150,
                        alignment: "center",
                        margin: [0, 0, 0, 10],
                      }
                    : {
                        text: "Kein Hero-Bild vorhanden",
                        style: "subheader",
                        alignment: "center",
                      },
                  thumbnailImages.length
                    ? this.buildThumbnailColumns(thumbnailImages)
                    : {},
                ],
              },
              {
                width: "67%",
                stack: [
                  this.buildVehicleAndMastRow(),
                  this.buildDescriptionAndExtrasRow(attachments),
                  this.buildBemerkungRow(),
                  this.buildContactAndPriceRow(contactImageBase64),
                ],
              },
            ],
            columnGap: 10,
          },
        ],
        footer: () => {
          return {
            // Außenabstand links/rechts bleibt erhalten
            margin: [15, -12, 15, 0],
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    // Innere Tabelle mit grauem Hintergrund
                    table: {
                      widths: ["*", "auto"],
                      body: [
                        [
                          {
                            stack: [
                              {
                                text: footerPrivacy,
                                style: "footerSmall",
                                margin: [0, 10, 0, 10],
                              },
                              {
                                text:
                                  printDateText +
                                  "  |  " +
                                  new Date().getFullYear() +
                                  " © Sander Fördertechnik GmbH",
                                style: "footerMain",
                              },
                            ],
                            fillColor: "#4d4d4d",
                            margin: [15, 10, 15, 10],
                            alignment: "left",
                          },
                          {
                            stack: [
                              qrCodeBase64
                                ? {
                                    image: qrCodeBase64,
                                    width: 80,
                                    alignment: "right",
                                    margin: [0, -40, 5, 5],
                                  }
                                : {},
                              machineLink
                                ? {
                                    text: machineLink,
                                    link: machineLink,
                                    alignment: "center",
                                    color: "#ffffff",
                                    decoration: "underline",
                                    fontSize: 8,
                                    margin: [0, 0, 5, 10],
                                  }
                                : {},
                            ],
                            fillColor: "#4d4d4d",
                            margin: [15, 10, 15, 20],
                            alignment: "right",
                          },
                        ],
                      ],
                    },
                    layout: "noBorders",
                  },
                ],
                // Leere Zeile für den zusätzlichen weißen Abstand unten
                [
                  {
                    text: "",
                    margin: [0, 0, 0, 15],
                    border: [false, false, false, false],
                  },
                ],
              ],
            },
            layout: "noBorders",
          };
        },

        styles: {
          header: {
            fontSize: 18,
            bold: true,
            color: "#4d4d4d",
            margin: [0, 0, 0, 10],
          },
          sectionHeader: {
            fontSize: 10,
            bold: true,
            margin: [0, 5, 0, 5],
            color: primaryColor,
          },
          dataLabel: {
            fontSize: 8,
            bold: true,
            color: "black",
          },
          dataValue: {
            fontSize: 8,
            color: "grey",
          },
          subheader: {
            fontSize: 12,
            bold: true,
            margin: [0, 2, 0, 5],
            color: primaryColor,
          },
          contactName: {
            fontSize: 8,
            bold: true,
            color: "black",
          },
          contactTitle: {
            fontSize: 8,
            color: "grey",
          },
          priceMain: {
            fontSize: 14,
            bold: true,
            color: primaryColor,
          },
          priceSub: {
            fontSize: 8,
            color: "grey",
          },
          priceNote: {
            fontSize: 8,
            color: "grey",
          },
          listValue: {
            fontSize: 8,
            color: "grey",
          },
          footerMain: {
            fontSize: 8,
            color: "#ffffff",
          },
          footerSmall: {
            fontSize: 6,
            color: "#ffffff",
          },
        },
      };

      // PDF erstellen + Download
      pdfMake.createPdf(docDefinition).download("Maschinendetails.pdf");
    },
  },
};
</script>
